import { useState, useEffect, useRef, useMemo } from "react";
import { IoPersonCircle } from "react-icons/io5";
import { FaRegTrashAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFileAudio, FaFileVideo, FaFileArchive, FaFile } from "react-icons/fa";
import { FiDownload } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdOutlineArrowDropDown, MdSend } from "react-icons/md";
import { BsPaperclip, BsThreeDotsVertical } from "react-icons/bs";
import useDrivePicker from "react-google-drive-picker";
import { LiaGoogleDrive } from "react-icons/lia";
import toast from "react-hot-toast";
import { FaUsers } from "react-icons/fa";

// Import your custom SunEditor wrapper component
import SunEditorComponent from "../chat/SunEditorComponent";

// You need to ensure these are correctly imported from your project's context and utils.
// For the sake of this example, I'm providing a placeholder structure.
import { DataState } from "../../context/DataProvider";
import { draftReplyEmail, replyToEmail } from "../../services/email_api";
import { google_client_id, google_api_key } from "../../utils/keys";


const getFileIcon = (mimeType) => {
    if (mimeType?.includes("pdf")) return <FaFilePdf className="text-red-500" size={20} />;
    if (mimeType?.includes("word") || mimeType?.includes("officedocument.wordprocessingml")) return <FaFileWord className="text-[#5652CF]" size={20} />;
    if (mimeType?.includes("excel") || mimeType?.includes("officedocument.spreadsheetml")) return <FaFileExcel className="text-green-500" size={20} />;
    if (mimeType?.includes("image")) return <FaFileImage className="text-yellow-500" size={20} />;
    if (mimeType?.includes("zip") || mimeType?.includes("rar") || mimeType?.includes("archive")) return <FaFileArchive className="text-gray-500" size={20} />;
    if (mimeType?.includes("audio")) return <FaFileAudio className="text-purple-500" size={20} />;
    if (mimeType?.includes("video")) return <FaFileVideo className="text-indigo-500" size={20} />;
    return <FaFile className="text-gray-500" size={20} />;
};

const AttachmentItem = ({ file, onRemove }) => {
    const [hovered, setHovered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadClick = async () => {
        if (isDownloading || file.from !== "local") return;
        setIsDownloading(true);

        try {
            const url = URL.createObjectURL(file.file);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Download failed. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div
            className="inline-flex items-center justify-center w-40 h-10 p-2 py-3 text-gray-800 border border-gray-300 rounded-lg shadow-md cursor-pointer"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {hovered && file.from === "local" ? (
                <div className="flex items-center justify-center w-full gap-4">
                    {isDownloading ? (
                        <AiOutlineLoading3Quarters className="text-gray-600 animate-spin" size={16} />
                    ) : (
                        <FiDownload
                            onClick={handleDownloadClick}
                            className="text-gray-600 cursor-pointer hover:text-black"
                            size={16}
                        />
                    )}
                    <button onClick={() => onRemove(file)} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                </div>
            ) : (
                <div className="flex items-center w-full space-x-2">
                    <div className="flex items-center justify-center w-8 h-8">
                        {getFileIcon(file?.file.mimeType || file?.file.type)}
                    </div>
                    <span className="text-sm font-medium truncate">
                        {file?.file.filename || file?.file.name}
                    </span>
                    {hovered && (
                        <button onClick={() => onRemove(file)} className="ml-auto text-gray-600 hover:text-red-400">&times;</button>
                    )}
                </div>
            )}
        </div>
    );
};

const ReplyEmail = ({
    onClose,
    sender,
    message_id,
    messageBody,
    setMessageBody,
    localAttachments,
    driveAttachments,
    setLocalAttachments,
    setDriveAttachments,
    customPrompt,
    setCustomPrompt,
    isOutlook,
}) => {
    const fileInputRef = useRef(null);
    const [recepientsEmails, setRecepientsEmails] = useState(sender?.map(s => s.email) || []);
    const [ccs, setCcs] = useState([]);
    const [bccs, setBccs] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [inputCcValue, setInputCcValue] = useState("");
    const [inputBccValue, setInputBccValue] = useState("");
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [loading, setLoading] = useState(false);
    const [customPromptLoading, setCustomPromptLoading] = useState(false);
    const [openPicker] = useDrivePicker();
    const { userInfo, userGoogleId } = DataState();

    // Just generated Content from AI
    const [justUpdatedValue, setJustUpdatedValue] = useState("");

    // Consolidated attachments for display
    const allAttachments = useMemo(() => {
        const local = localAttachments.map(file => ({ name: file.name, from: "local", file }));
        const drive = driveAttachments.map(file => ({ name: file.name, from: "drive", id: file.id, file }));
        return [...local, ...drive];
    }, [localAttachments, driveAttachments]);

    const removeEmail = (emailToRemove, type) => {
        if (type === "to") {
            setRecepientsEmails(prev => prev.filter((email) => email !== emailToRemove));
        } else if (type === "cc") {
            setCcs(prev => prev.filter((email) => email !== emailToRemove));
        } else if (type === "bcc") {
            setBccs(prev => prev.filter((email) => email !== emailToRemove));
        }
    };

    const handleKeyDown = (event, type) => {
        const value = (type === "to" ? inputValue : type === "cc" ? inputCcValue : inputBccValue).trim().replace(/,$/, "");
        if ((event.key === "Enter" || event.key === "," || event.key === " ") && value) {
            event.preventDefault();
            const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
            const match = value.match(emailPattern);

            if (match) {
                if (type === "to") {
                    if (!recepientsEmails.includes(value)) setRecepientsEmails(prev => [...prev, value]);
                    setInputValue("");
                } else if (type === "cc") {
                    if (!ccs.includes(value)) setCcs(prev => [...prev, value]);
                    setInputCcValue("");
                } else if (type === "bcc") {
                    if (!bccs.includes(value)) setBccs(prev => [...prev, value]);
                    setInputBccValue("");
                }
            } else {
                toast.error("Invalid email format!");
            }
        }
    };

    const handleReply = async () => {
        console.log("Replying to email...");
        const hasMessage = messageBody && messageBody.trim() !== "";
        const hasLocalFiles = localAttachments.length > 0;
        const hasDriveFiles = driveAttachments.length > 0;

        if (!hasMessage && !hasLocalFiles && !hasDriveFiles) {
            toast("Nothing to send. Please add a message or attachment.");
            return;
        }
        if (recepientsEmails.length === 0) {
            toast.error("Please add at least one recipient.");
            return;
        }
        setLoading(true);

        const formData = new FormData();
        recepientsEmails.forEach(email => formData.append("recipients", email));
        ccs.forEach(email => formData.append("ccs", email));
        bccs.forEach(email => formData.append("bccs", email));

        formData.append("message_id", message_id);
        if (messageBody) {
            formData.append("message_body", messageBody);
        }
        if (localAttachments.length > 0) {
            localAttachments.forEach((file) => {
                formData.append("files", file);
            });
        }
        const driveFileIds = driveAttachments.map(file => file.id);
        driveFileIds.forEach(id => formData.append("google_drive_file_ids", id));

        try {
            await replyToEmail(userGoogleId, formData, isOutlook);
            toast.success("Reply sent successfully!");
            setMessageBody("");
            setJustUpdatedValue("clearing")
            setJustUpdatedValue("");

            setLocalAttachments([]);
            setDriveAttachments([]);
            setCustomPrompt("");
            onClose(); // Close the modal after a successful send
        } catch (error) {
            console.error("Reply Error:", error);
            toast.error(error.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftReply = async () => {
        if (!customPrompt.trim()) {
            toast.error("Please provide a prompt to generate a draft.");
            return;
        }
        setCustomPromptLoading(true);
        try {
            const response = await draftReplyEmail(userGoogleId, message_id, customPrompt, isOutlook);

            const htmlBody = response?.html_body || "";

            setMessageBody(htmlBody);
            setJustUpdatedValue(htmlBody);

        } catch (error) {
            console.error("Error fetching draft reply:", error);
            toast.error(error.message || "Something went wrong. Please try again.");
        } finally {
            setCustomPromptLoading(false);
        }
    };


    const handlePaperClipClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setLocalAttachments((prevAttachments) => [...prevAttachments, ...files]);
        event.target.value = null; // Reset the input so the same file can be selected again
    };

    const handleRemove = (fileToRemove) => {
        if (fileToRemove.from === "local") {
            setLocalAttachments((prev) => prev.filter((file) => file.name !== fileToRemove.name));
        } else if (fileToRemove.from === "drive") {
            setDriveAttachments((prev) => prev.filter((file) => file.id !== fileToRemove.id));
        }
    };

    const handleTrashClick = () => {
        if (!messageBody && !customPrompt && !localAttachments.length && !driveAttachments.length) {
            onClose();
            return;
        }
        setMessageBody("");
        setJustUpdatedValue("clearing")
        setJustUpdatedValue("");
        
        setCustomPrompt("");
        setLocalAttachments([]);
        setDriveAttachments([]);
        onClose();
    };

    const handleOpenPicker = () => {
        openPicker({
            clientId: google_client_id,
            developerKey: google_api_key,
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: true,
            callbackFunction: (data) => {
                if (data.action === "picked") {
                    const files = data.docs.map((file) => ({
                        id: file.id,
                        name: file.name,
                        mimeType: file?.mimeType || file?.type,
                        url: file.url,
                    }));
                    setDriveAttachments((prev) => [...prev, ...files]);
                }
            },
        });
    };

    return (
        <div className="flex flex-row w-full transition duration-200">
            <div className="px-4 pl-0">
                {userInfo?.picture ? (
                    <img src={userInfo.picture} alt="User Profile" className="object-cover w-10 h-10 rounded-full" />
                ) : (
                    <IoPersonCircle className="text-gray-700" size={40} />
                )}
            </div>
            <div className="flex flex-col w-full">
                <div className="w-full p-4 bg-white border border-gray-300 shadow-md rounded-xl">

                    {/* Corrected structure for the recipients and Cc/Bcc buttons */}
                    <div className="flex flex-row items-start w-full gap-2 mb-3 text-gray-800">
                        <FaUsers className="self-center hidden w-5 h-5 mt-2 text-gray-600 sm:block" />

                        <div className="flex flex-wrap items-center flex-grow w-full gap-1 p-2 text-sm border border-gray-300 rounded-lg min-h-[40px]">
                            {recepientsEmails.map((email, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg">
                                    {email}
                                    <button onClick={() => removeEmail(email, "to")} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="flex-grow min-w-[120px] bg-transparent outline-none text-gray-800"
                                placeholder="Enter recipients..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "to")}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={() => setShowCc(!showCc)} className={`flex items-center justify-center text-sm border border-gray-300 py-1 px-2 rounded-lg min-h-[40px] hover:bg-gray-200 transition-colors duration-200 ${showCc ? "bg-gray-200" : "bg-white"} w-full sm:w-auto text-center`}>
                                Cc
                            </button>
                            <button onClick={() => setShowBcc(!showBcc)} className={`flex items-center justify-center text-sm border border-gray-300 py-1 px-2 rounded-lg min-h-[40px] hover:bg-gray-200 transition-colors duration-200 ${showBcc ? "bg-gray-200" : "bg-white"} w-full sm:w-auto text-center`}>
                                Bcc
                            </button>
                        </div>
                    </div>

                    {showCc && (
                        <div className="flex flex-wrap items-center gap-1 text-sm border border-gray-300 p-2 rounded-lg w-full min-h-[40px] mb-3">
                            {ccs.map((email, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg">
                                    {email}
                                    <button onClick={() => removeEmail(email, "cc")} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="bg-transparent outline-none text-gray-800 flex-grow min-w-[120px]"
                                placeholder="Cc..."
                                value={inputCcValue}
                                onChange={(e) => setInputCcValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "cc")}
                            />
                        </div>
                    )}

                    {showBcc && (
                        <div className="flex flex-wrap items-center gap-1 text-sm border border-gray-300 p-2 rounded-lg w-full min-h-[40px] mb-3">
                            {bccs.map((email, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 text-gray-800 bg-gray-200 rounded-lg">
                                    {email}
                                    <button onClick={() => removeEmail(email, "bcc")} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                                </span>
                            ))}
                            <input
                                type="text"
                                className="bg-transparent outline-none text-gray-800 flex-grow min-w-[120px]"
                                placeholder="Bcc..."
                                value={inputBccValue}
                                onChange={(e) => setInputBccValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, "bcc")}
                            />
                        </div>
                    )}

                    {/* The new SunEditorComponent replacement */}
                    <div className="mb-4">
                        <SunEditorComponent
                            value={messageBody}
                            onChange={setMessageBody}
                            justUpdatedValue={justUpdatedValue}
                        />
                    </div>

                    {allAttachments.length > 0 && (
                        <div className="flex flex-wrap items-center flex-grow gap-2 p-1 mt-2">
                            {allAttachments.map((file, index) => (
                                <AttachmentItem key={index} file={file} onRemove={handleRemove} />
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col items-center justify-between mt-3 text-gray-800">
                        <div className="flex flex-col items-stretch justify-between w-full space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
                            <input
                                type="text"
                                name="custom prompt"
                                value={customPrompt}
                                placeholder="Add prompt for AI-generated reply draft..."
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                className="w-full p-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300"
                            />
                            <button
                                onClick={handleDraftReply}
                                disabled={customPromptLoading}
                                className="flex items-center justify-center w-full px-4 py-2 space-x-2 text-sm text-white transition border rounded-md sm:w-auto bg-zinc-700 border-zinc-600 hover:border-zinc-800"
                            >
                                <img src="/assets/logo.png" alt="Logo" className="w-5 h-5" />
                                {customPromptLoading ? (
                                    <span>Generating...</span>
                                ) : (
                                    <span>Generate</span>
                                )}
                            </button>
                        </div>

                        <div className="flex items-center justify-between w-full gap-2 p-2 pl-0 pr-0 space-x-3 sm:gap-0">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={handleReply}
                                    disabled={loading || recepientsEmails.length === 0}
                                    className="flex items-center justify-between px-2 font-medium text-white transition-colors bg-[#5652CF] rounded-full hover:bg-gray-600"
                                >
                                    <div className="flex items-center justify-center h-full pl-1">
                                        <MdSend size={20} />
                                    </div>
                                    <div className="py-1 pl-2 pr-4 ">
                                        {loading ? "Sending..." : "Send"}
                                    </div>
                                </button>
                                <div className="flex">
                                    <div onClick={handlePaperClipClick} className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200">
                                        <BsPaperclip size={18} />
                                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} multiple />
                                    </div>
                                    <div onClick={handleOpenPicker} className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200">
                                        <LiaGoogleDrive size={18} />
                                    </div>
                                    <div className="flex items-center justify-center w-8 h-8 text-gray-600 transition-colors rounded-md cursor-pointer hover:text-gray-900 hover:bg-gray-200">
                                        <BsThreeDotsVertical size={18} />
                                    </div>
                                </div>
                            </div>
                            <div className="w-auto ml-auto">
                                <button onClick={handleTrashClick} className="text-gray-600 transition-colors hover:text-gray-900">
                                    <FaRegTrashAlt size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReplyEmail;